/**
 * Profile Incomplete Modal
 * Modal shown when user tries to create a ritual with incomplete profile
 */

import { ChevronRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { CheckmarkIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { User as UserType } from "@/features/auth/store/authStore"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { getAllProfileItems } from "../utils/profileCompletion"

interface ProfileIncompleteModalProps {
  isOpen: boolean
  user: UserType | null
  closeOnly?: boolean
  onClose?: (wasItemClick?: boolean) => void
}

export function ProfileIncompleteModal({
  isOpen,
  user,
  closeOnly = false,
  onClose,
}: ProfileIncompleteModalProps) {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { t } = useTranslation("profile")

  const allItems = user ? getAllProfileItems(user) : []

  const profileKeyToTabMap: Record<string, string> = {
    coverPhoto: "photos",
    bio: "profile-info",
    spiritualTraditions: "traditions",
    ethnicBackground: "traditions",
    intentions: "intentions",
  }

  const handleItemClick = (key: string) => {
    const tab = profileKeyToTabMap[key] ?? "profile-info"
    navigate(`/profile/edit#tab=${tab}`)
    onClose?.(true)
  }

  const handleGoBack = () => {
    if (closeOnly) {
      // Close the modal via callback - parent controls isOpen state
      onClose?.()
    } else {
      navigate(-1)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleGoBack()}>
      <DialogContent
        showCloseButton={!isMobile}
        className="px-5 pt-8.75 md:p-15"
        overlayClass="bg-[#020202] md:bg-[rgb(0,0,0,0.6)]"
      >
        <DialogHeader className="text-center">
          <DialogTitle className="text-primary text-2xl font-semibold text-center">
            {t("profileCompletion.title")}
          </DialogTitle>
          <DialogDescription className="text-primary text-center text-sm leading-5.5">
            {t("profileCompletion.description")} <br />
            {t("profileCompletion.description2")}
          </DialogDescription>
        </DialogHeader>

        {/* Checklist Items */}
        <div className="space-y-2.5 md:space-y-3 py-3">
          {allItems.map(item => (
            <div
              key={item.key}
              onClick={() => handleItemClick(item.key)}
              className={`flex items-center justify-between px-3.75 py-2.5 md:p-3 cursor-pointer rounded-lg border ${item.isComplete ? "bg-brand-olive-alpha-40 border-brand-olive" : "bg-card border-input"}`}
            >
              <div className="flex w-full items-center justify-between gap-3 p-1">
                <span
                  className={`text-[13px] leading-5 md:text-sm font-medium ${
                    item.isComplete ? "text-primary line-through" : "text-primary font-semibold"
                  }`}
                >
                  {item.label}
                </span>

                {item.isComplete ? (
                  <div className="min-w-7 min-h-7 rounded-full bg-white flex items-center justify-center">
                    <CheckmarkIcon className="h-5 w-5 text-[#000000]" />
                  </div>
                ) : (
                  <div className="min-w-5 min-h-5 flex items-center justify-center">
                    <ChevronRight className="cursor-pointer" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-3 flex items-center !justify-center">
          <div className="flex items-center justify-center">
            <Button
              variant="primary"
              onClick={handleGoBack}
              className="flex-1 rounded-full uppercase px-15"
            >
              {t("profileCompletion.maybeLater")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
